import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DeviceHub as DeviceHubIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Device, deviceService } from '../api/devices';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const getStatusColor = (status: Device['status']) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'warning';
    case 'REGISTERED':
      return 'info';
    case 'DEACTIVATED':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: Device['status']) => {
  switch (status) {
    case 'ACTIVE':
      return 'アクティブ';
    case 'INACTIVE':
      return '非アクティブ';
    case 'REGISTERED':
      return '登録済み';
    case 'DEACTIVATED':
      return '無効化済み';
    default:
      return status;
  }
};

interface DeviceDetailDialogProps {
  device: Device | null;
  open: boolean;
  onClose: () => void;
}

const DeviceDetailDialog: React.FC<DeviceDetailDialogProps> = ({ device, open, onClose }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const deactivateMutation = useMutation({
    mutationFn: (deviceId: string) => deviceService.deactivateDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      enqueueSnackbar('デバイスを無効化しました', { variant: 'success' });
      onClose();
    },
    onError: () => {
      enqueueSnackbar('デバイスの無効化に失敗しました', { variant: 'error' });
    }
  });

  const activateMutation = useMutation({
    mutationFn: ({ deviceId, licenseId }: { deviceId: string; licenseId: string }) =>
      deviceService.activateDevice(deviceId, licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      enqueueSnackbar('デバイスをアクティベートしました', { variant: 'success' });
      onClose();
    },
    onError: () => {
      enqueueSnackbar('デバイスのアクティベートに失敗しました', { variant: 'error' });
    }
  });

  if (!device) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DeviceHubIcon />
          デバイス詳細
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              デバイスID
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {device.id}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              ハードウェアID
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {device.hardwareId}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              ホスト名
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {device.hostname || '-'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              プラットフォーム
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {device.platform || '-'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              ステータス
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip
                label={getStatusLabel(device.status)}
                color={getStatusColor(device.status)}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              ライセンスID
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {device.licenseId || '未割り当て'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              ユーザー名
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {device.username || '-'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              登録日時
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {device.createdAt && device.createdAt !== ''
                ? format(new Date(device.createdAt), 'yyyy年MM月dd日 HH:mm', { locale: ja })
                : '-'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              最終ハートビート
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {device.lastHeartbeat && device.lastHeartbeat !== ''
                ? format(new Date(device.lastHeartbeat), 'yyyy年MM月dd日 HH:mm', { locale: ja })
                : '未受信'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              最終オンライン
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {device.lastOnlineAt && device.lastOnlineAt !== ''
                ? format(new Date(device.lastOnlineAt), 'yyyy年MM月dd日 HH:mm', { locale: ja })
                : '-'}
            </Typography>
          </Grid>

          {device.metadata && Object.keys(device.metadata).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                メタデータ
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                  {JSON.stringify(device.metadata, null, 2)}
                </pre>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {device.status === 'ACTIVE' && (
          <Button
            onClick={() => deactivateMutation.mutate(device.id)}
            color="error"
            disabled={deactivateMutation.isPending}
            startIcon={deactivateMutation.isPending ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            無効化
          </Button>
        )}
        <Button onClick={onClose} color="inherit">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Devices: React.FC = () => {
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const { data: devices, isLoading, error, refetch } = useQuery({
    queryKey: ['devices'],
    queryFn: deviceService.getDevices,
    refetchInterval: 30000
  });

  const deleteMutation = useMutation({
    mutationFn: deviceService.deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      enqueueSnackbar('デバイスが削除されました', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      enqueueSnackbar(errorData?.error || 'デバイスの削除に失敗しました', { variant: 'error' });
      console.error('Failed to delete device:', error);
    }
  });

  const handleShowDetail = (device: Device) => {
    setSelectedDevice(device);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedDevice(null);
  };

  const handleRefresh = () => {
    refetch();
    enqueueSnackbar('デバイス一覧を更新しました', { variant: 'info' });
  };

  const handleDeleteClick = (device: Device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deviceToDelete) {
      deleteMutation.mutate(deviceToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          デバイス一覧の取得に失敗しました。
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          デバイス管理
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="更新">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {devices && devices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DeviceHubIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            登録されているデバイスはありません
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            デバイスがライセンスをアクティベートすると、ここに表示されます。
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>デバイスID</TableCell>
                <TableCell>ホスト名</TableCell>
                <TableCell>プラットフォーム</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>ライセンスID</TableCell>
                <TableCell>ユーザー名</TableCell>
                <TableCell>最終ハートビート</TableCell>
                <TableCell align="center">アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices?.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {device.id ? `${device.id.substring(0, 12)}...` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{device.hostname || '-'}</TableCell>
                  <TableCell>{device.platform || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(device.status)}
                      color={getStatusColor(device.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {device.licenseId && typeof device.licenseId === 'string' ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {device.licenseId.substring(0, 8)}...
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{device.username || '-'}</TableCell>
                  <TableCell>
                    {device.lastHeartbeat && device.lastHeartbeat !== ''
                      ? format(new Date(device.lastHeartbeat), 'MM/dd HH:mm', { locale: ja })
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="詳細表示">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleShowDetail(device)}
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(device)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeviceDetailDialog
        device={selectedDevice}
        open={detailDialogOpen}
        onClose={handleCloseDetail}
      />

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>デバイスの削除確認</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            本当にこのデバイスを削除しますか？
          </Typography>
          {deviceToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>デバイスID:</strong> {deviceToDelete.id}</Typography>
              <Typography variant="body2"><strong>ハードウェアID:</strong> {deviceToDelete.hardwareId}</Typography>
              <Typography variant="body2"><strong>デバイス名:</strong> {deviceToDelete.name || '-'}</Typography>
              <Typography variant="body2"><strong>ステータス:</strong> {getStatusLabel(deviceToDelete.status)}</Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            この操作は取り消すことができません。関連するアクティベーション記録も削除されます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Devices; 